import React, { useState, useEffect } from 'react'
import { supabase } from '../supabase'

const MONTHS_ES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic']
const MONTHS_FULL = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre']

function fmt$(n) { return '$' + (Number(n)||0).toLocaleString('en-US',{minimumFractionDigits:0}) }

export default function Stats() {
  const [data, setData] = useState({ financials:[], clients:[], jobs:[] })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const now = new Date()

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    setLoading(true)
    try {
      const yearStart = now.getFullYear() + '-01-01'
      const [f, c, j] = await Promise.all([
        supabase.from('financials').select('amount,type,entry_date').gte('entry_date', yearStart),
        supabase.from('clients').select('id,name,city,frequency,is_active'),
        supabase.from('jobs').select('id,client_id,scheduled_date').gte('scheduled_date', yearStart),
      ])
      setData({ financials: f.data||[], clients: c.data||[], jobs: j.data||[] })
    } catch(e) {
      setError('Error: ' + String(e.message || e))
    }
    setLoading(false)
  }

  if (loading) return <div style={{padding:32,textAlign:'center',color:'#9aa0a6'}}>Cargando... 🌸</div>
  if (error) return <div style={{padding:32,textAlign:'center',color:'#a32d2d'}}>{error}</div>

  const { financials, clients, jobs } = data

  // Revenue/expense by month
  const revByMonth = Array(12).fill(0)
  const expByMonth = Array(12).fill(0)
  financials.forEach(f => {
    const m = new Date(String(f.entry_date)+'T12:00:00').getMonth()
    if (isNaN(m)) return
    if (f.type === 'income') revByMonth[m] += Number(f.amount)||0
    else expByMonth[m] += Number(f.amount)||0
  })

  // Jobs by month
  const jobsByMonth = Array(12).fill(0)
  jobs.forEach(j => {
    const m = new Date(String(j.scheduled_date)+'T12:00:00').getMonth()
    if (!isNaN(m)) jobsByMonth[m]++
  })

  // This week
  const dow = now.getDay()
  const mon = new Date(now); mon.setDate(now.getDate()-(dow===0?6:dow-1)); mon.setHours(0,0,0,0)
  const sun = new Date(mon); sun.setDate(mon.getDate()+6); sun.setHours(23,59,59,999)
  const weekJobs = jobs.filter(j => { const d=new Date(String(j.scheduled_date)+'T12:00:00'); return d>=mon&&d<=sun }).length
  const weekRev = financials.filter(f => {
    const d=new Date(String(f.entry_date)+'T12:00:00')
    return f.type==='income' && d>=mon && d<=sun
  }).reduce((s,f)=>s+Number(f.amount||0),0)

  const thisMonth = now.getMonth()
  const monthRev = revByMonth[thisMonth]
  const monthExp = expByMonth[thisMonth]
  const monthJobs = jobsByMonth[thisMonth]
  const totalRev = revByMonth.reduce((a,b)=>a+b,0)
  const totalExp = expByMonth.reduce((a,b)=>a+b,0)
  const totalJobs = jobsByMonth.reduce((a,b)=>a+b,0)

  // Top clients
  const jobCount = {}
  jobs.forEach(j => { jobCount[String(j.client_id)] = (jobCount[String(j.client_id)]||0)+1 })
  const topClients = Object.entries(jobCount)
    .sort((a,b)=>b[1]-a[1]).slice(0,5)
    .map(([id,count]) => ({ c: clients.find(x=>x.id===id), count }))
    .filter(x=>x.c)

  // By area
  const byArea = {}
  clients.forEach(c => {
    const area = String(c.city||'').split(',')[0].trim() || 'Sin ciudad'
    byArea[area] = (byArea[area]||0)+1
  })
  const topAreas = Object.entries(byArea).sort((a,b)=>b[1]-a[1]).slice(0,5)

  // Frequency
  const freqCount = {}
  clients.forEach(c => { freqCount[String(c.frequency||'?')] = (freqCount[String(c.frequency||'?')]||0)+1 })
  const freqLabels = {weekly:'Semanal',biweekly:'Quincenal',monthly:'Mensual','one-time':'Una vez',triweekly:'Cada 3 sem',fortweekly:'Cada 4 sem',bimonthly:'Bimestral',custom:'Personalizado'}

  // Client source breakdown
  const sourceCount = {}
  const sourceLabels = {referral:'Referido',instagram:'Instagram',facebook:'Facebook',yelp:'Yelp',nextdoor:'Nextdoor',google:'Google',flyer:'Volante',repeat:'Recurrente',other:'Otro','':'Desconocido'}
  clients.forEach(c => { const src = c.source||''; sourceCount[src]=(sourceCount[src]||0)+1 })
  const sourceColors = {referral:'#1d9e75',instagram:'#e8547a',facebook:'#1877f2',yelp:'#d32323',nextdoor:'#00b347',google:'#4285f4',flyer:'#f59e0b',repeat:'#7F77DD','':'#9aa0a6',other:'#80868b'}

  const maxRev = Math.max(...revByMonth, 1)
  const maxJobs = Math.max(...jobsByMonth, 1)
  const activeClients = clients.filter(c=>c.is_active).length

  const kpis = [
    { label:'Esta semana', sub:'Limpiezas',      val:weekJobs,  fmt:'num',   color:'#185fa5' },
    { label:'Esta semana', sub:'Ingresos',        val:weekRev,   fmt:'money', color:'#1d9e75' },
    { label:MONTHS_FULL[thisMonth], sub:'Ingresos',  val:monthRev,  fmt:'money', color:'#1d9e75' },
    { label:MONTHS_FULL[thisMonth], sub:'Gastos',    val:monthExp,  fmt:'money', color:'#e24b4a' },
    { label:MONTHS_FULL[thisMonth], sub:'Limpiezas', val:monthJobs, fmt:'num',   color:'#185fa5' },
    { label:'Año '+now.getFullYear(), sub:'Ingresos totales', val:totalRev, fmt:'money', color:'#1d9e75' },
    { label:'Año '+now.getFullYear(), sub:'Limpiezas totales',val:totalJobs,fmt:'num',  color:'#185fa5' },
    { label:'Total', sub:'Clientes activos', val:activeClients, fmt:'num', color:'#534AB7' },
  ]

  return (
    <div style={s.page}>
      <div style={s.pageHead}>
        <div style={s.pageTitle}>📊 Estadísticas</div>
        <div style={s.pageSub}>Año {now.getFullYear()}</div>
      </div>

      <div style={s.kpiRow}>
        {kpis.map((k,i) => (
          <div key={i} style={s.kpi}>
            <div style={s.kpiSub}>{String(k.label)}</div>
            <div style={{...s.kpiVal, color:k.color}}>
              {k.fmt==='money' ? fmt$(k.val) : String(k.val)}
            </div>
            <div style={s.kpiLabel}>{String(k.sub)}</div>
          </div>
        ))}
      </div>

      <div style={s.gridTwo}>
        {/* Revenue chart */}
        <div style={s.panel}>
          <div style={s.panelTitle}>Ingresos por mes</div>
          <div style={s.barChart}>
            {revByMonth.map((v,i) => (
              <div key={i} style={s.barCol}>
                <div style={s.barWrap}>
                  <div style={{...s.bar, height:`${(v/maxRev)*100}%`, background:'#e8547a', opacity:i===thisMonth?1:.45}}/>
                </div>
                <div style={s.barLabel}>{MONTHS_ES[i]}</div>
                {v>0 && <div style={s.barVal}>${Math.round(v/100)/10}k</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Jobs chart */}
        <div style={s.panel}>
          <div style={s.panelTitle}>Limpiezas por mes</div>
          <div style={s.barChart}>
            {jobsByMonth.map((v,i) => (
              <div key={i} style={s.barCol}>
                <div style={s.barWrap}>
                  <div style={{...s.bar, height:`${(v/maxJobs)*100}%`, background:'#185fa5', opacity:i===thisMonth?1:.45}}/>
                </div>
                <div style={s.barLabel}>{MONTHS_ES[i]}</div>
                {v>0 && <div style={s.barVal}>{String(v)}</div>}
              </div>
            ))}
          </div>
        </div>

        {/* Top clients */}
        <div style={s.panel}>
          <div style={s.panelTitle}>Top clientes por limpiezas</div>
          {topClients.length===0
            ? <div style={s.empty}>Sin datos aún — agrega limpiezas para ver estadísticas</div>
            : topClients.map((item,i) => (
                <div key={i} style={s.listRow}>
                  <div style={s.listRank}>#{i+1}</div>
                  <div style={{flex:1}}>
                    <div style={s.listName}>{String(item.c.name||'')}</div>
                    <div style={s.listSub}>{String(item.c.city||'Sin ciudad').split(',')[0]}</div>
                  </div>
                  <div style={s.listVal}>{String(item.count)} limpiezas</div>
                </div>
              ))
          }
        </div>

        {/* By area */}
        <div style={s.panel}>
          <div style={s.panelTitle}>Clientes por área</div>
          {topAreas.length===0
            ? <div style={s.empty}>Agrega ciudades a los perfiles de clientes</div>
            : topAreas.map(([area,count],i) => (
                <div key={i} style={s.listRow}>
                  <div style={s.listRank}>#{i+1}</div>
                  <div style={{flex:1}}><div style={s.listName}>{String(area)}</div></div>
                  <div style={{display:'flex',alignItems:'center',gap:8}}>
                    <div style={{width:60,height:6,background:'#e3e5e8',borderRadius:3,overflow:'hidden'}}>
                      <div style={{height:'100%',background:'#e8547a',width:`${clients.length>0?(count/clients.length)*100:0}%`,borderRadius:3}}/>
                    </div>
                    <div style={s.listVal}>{String(count)}</div>
                  </div>
                </div>
              ))
          }
        </div>

        {/* Frequency */}
        <div style={s.panel}>
          <div style={s.panelTitle}>Clientes por frecuencia</div>
          {Object.entries(freqCount).sort((a,b)=>b[1]-a[1]).map(([freq,count]) => (
            <div key={freq} style={s.listRow}>
              <div style={{flex:1}}><div style={s.listName}>{String(freqLabels[freq]||freq)}</div></div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <div style={{width:80,height:6,background:'#e3e5e8',borderRadius:3,overflow:'hidden'}}>
                  <div style={{height:'100%',background:'#534AB7',width:`${clients.length>0?(count/clients.length)*100:0}%`,borderRadius:3}}/>
                </div>
                <div style={s.listVal}>{String(count)} clientes</div>
              </div>
            </div>
          ))}
        </div>

        {/* Client source */}
        <div style={s.panel}>
          <div style={s.panelTitle}>Clientes por fuente</div>
          {Object.entries(sourceCount).sort((a,b)=>b[1]-a[1]).map(([src,count]) => (
            <div key={src} style={s.listRow}>
              <div style={{flex:1}}><div style={{...s.listName,color:sourceColors[src]||'#80868b'}}>{String(sourceLabels[src]||src||'Desconocido')}</div></div>
              <div style={{display:'flex',alignItems:'center',gap:8}}>
                <div style={{width:80,height:6,background:'#e3e5e8',borderRadius:3,overflow:'hidden'}}>
                  <div style={{height:'100%',background:sourceColors[src]||'#9aa0a6',width:`${clients.length>0?(count/clients.length)*100:0}%`,borderRadius:3}}/>
                </div>
                <div style={s.listVal}>{String(count)}</div>
              </div>
            </div>
          ))}
          {Object.keys(sourceCount).length===0&&<div style={s.empty}>Agrega fuentes en los perfiles de clientes</div>}
        </div>

        {/* P&L */}
        <div style={s.panel}>
          <div style={s.panelTitle}>Resumen {MONTHS_FULL[thisMonth]}</div>
          <div style={s.plRow}><span style={s.plLabel}>Ingresos</span><span style={{...s.plVal,color:'#1d9e75'}}>{fmt$(monthRev)}</span></div>
          <div style={s.plRow}><span style={s.plLabel}>Gastos</span><span style={{...s.plVal,color:'#e24b4a'}}>-{fmt$(monthExp)}</span></div>
          <div style={{...s.plRow,borderTop:'2px solid #e3e5e8',paddingTop:10,marginTop:4}}>
            <span style={{...s.plLabel,fontWeight:600,color:'#202124'}}>Ganancia neta</span>
            <span style={{...s.plVal,fontWeight:600,color:(monthRev-monthExp)>=0?'#185fa5':'#e24b4a'}}>{fmt$(monthRev-monthExp)}</span>
          </div>
          <div style={{marginTop:14,padding:'10px 12px',background:'#f8f9fa',borderRadius:8}}>
            <div style={{fontSize:11,color:'#80868b',marginBottom:3}}>Acumulado {now.getFullYear()}</div>
            <div style={{fontSize:18,fontWeight:600,color:(totalRev-totalExp)>=0?'#185fa5':'#e24b4a'}}>{fmt$(totalRev-totalExp)}</div>
          </div>
        </div>
      </div>
    </div>
  )
}

const s = {
  page:{padding:'16px 20px',overflowY:'auto',height:'100%'},
  pageHead:{marginBottom:14},
  pageTitle:{fontSize:20,fontWeight:600,color:'#202124'},
  pageSub:{fontSize:12,color:'#9aa0a6',marginTop:2},
  kpiRow:{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:14},
  kpi:{background:'#fff',borderRadius:10,padding:'10px 12px',border:'1px solid #e3e5e8'},
  kpiSub:{fontSize:9,color:'#9aa0a6',textTransform:'uppercase',letterSpacing:'.05em'},
  kpiVal:{fontSize:20,fontWeight:600,lineHeight:1.2,marginTop:2},
  kpiLabel:{fontSize:10,color:'#80868b',marginTop:2},
  gridTwo:{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12},
  panel:{background:'#fff',borderRadius:12,border:'1px solid #e3e5e8',padding:'14px 16px'},
  panelTitle:{fontSize:13,fontWeight:600,color:'#202124',marginBottom:12},
  barChart:{display:'flex',gap:3,height:110,alignItems:'flex-end'},
  barCol:{flex:1,display:'flex',flexDirection:'column',alignItems:'center',gap:1},
  barWrap:{flex:1,width:'100%',display:'flex',alignItems:'flex-end',justifyContent:'center'},
  bar:{width:'75%',borderRadius:'3px 3px 0 0',minHeight:2},
  barLabel:{fontSize:8,color:'#9aa0a6'},
  barVal:{fontSize:8,color:'#80868b'},
  empty:{fontSize:12,color:'#9aa0a6',textAlign:'center',padding:'16px 0'},
  listRow:{display:'flex',alignItems:'center',gap:8,padding:'7px 0',borderBottom:'1px solid #f8f9fa'},
  listRank:{fontSize:11,color:'#9aa0a6',width:22,flexShrink:0},
  listName:{fontSize:12,fontWeight:500,color:'#202124'},
  listSub:{fontSize:10,color:'#9aa0a6',marginTop:1},
  listVal:{fontSize:12,color:'#5f6368',flexShrink:0,whiteSpace:'nowrap'},
  plRow:{display:'flex',justifyContent:'space-between',padding:'8px 0',borderBottom:'1px solid #f8f9fa'},
  plLabel:{fontSize:13,color:'#5f6368'},
  plVal:{fontSize:13,fontWeight:500},
}
